import { useEffect, useState, useRef } from 'react'
import { Link } from 'react-router-dom'
import { ExternalLink, Image as ImageIcon, Check } from 'lucide-react'
import { themesAPI } from '../../services/api'
import { useAdminBusiness } from '../../contexts/AdminBusinessContext'
import toast from 'react-hot-toast'
import { t } from '../../i18n'

// A real visual editor: change the text/image fields here and see the exact
// same styling your storefront uses update live, then save - no JSON file,
// no separate publish step. Full drag-and-drop page building (arbitrary new
// sections, moving blocks around) is a much larger project; this edits the
// fields every template already supports (hero text, hero image, colors).
export default function WebsitePage() {
  const { branch, businessType } = useAdminBusiness()
  const [theme, setTheme] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [form, setForm] = useState({ heroTitle: '', heroSubtitle: '', heroImage: '', colors: {} })
  const [uploadingImage, setUploadingImage] = useState(false)
  const fileInputRef = useRef(null)
  const website = branch.website_url || `/?branch=${branch.id}`

  const load = async () => {
    try {
      const { data } = await themesAPI.current()
      setTheme(data.theme)
      setForm({
        heroTitle: data.theme?.heroTitle || '',
        heroSubtitle: data.theme?.heroSubtitle || '',
        heroImage: data.theme?.heroImage || '',
        colors: data.theme?.colors || {}
      })
      setError('')
    } catch { setError(t("Could not load your saved design. Please try again.")) }
    finally { setLoading(false) }
  }
  useEffect(() => { load() }, [branch.id])

  const handleImageUpload = async (file) => {
    if (!file) return
    setUploadingImage(true)
    try {
      const { data } = await themesAPI.uploadImage(file)
      setForm(f => ({ ...f, heroImage: data.url }))
      toast.success(t("Image uploaded - remember to save"))
    } catch { toast.error(t("Could not upload that image")) }
    finally { setUploadingImage(false) }
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const { data } = await themesAPI.edit(form)
      setTheme(data.theme)
      toast.success(t("Your website has been updated"))
    } catch (e) { toast.error(t(e.response?.data?.error || 'Could not save your changes')) }
    finally { setSaving(false) }
  }

  const colorFields = [
    { key: 'primary', label: 'Primary', fallback: '#263f32' },
    { key: 'secondary', label: 'Secondary', fallback: '#eaece4' },
    { key: 'accent', label: 'Accent', fallback: '#9b6b32' },
  ]
  const templatesTab = businessType === 'restaurant' ? 'menu_templates' : businessType === 'ecommerce' ? 'store_templates' : 'booking_templates'
  const heroTitlePlaceholder = t(businessType === 'restaurant' ? 'Something worth gathering for.' : businessType === 'ecommerce' ? 'Find your next favorite.' : 'Make time for what matters.')

  if (loading) return <div className="max-w-5xl mx-auto p-6 text-slate-400">{t("Loading your website...")}</div>

  return <div className="max-w-5xl mx-auto space-y-8">
    <header className="flex flex-wrap justify-between items-start gap-4">
      <div>
        <p className="text-sm font-medium text-emerald-700 mb-2">{branch.name} · {t(businessType)}</p>
        <h1 className="text-3xl font-semibold text-slate-900">{t("Website & Themes")}</h1>
        <p className="text-slate-500 mt-3">{t("Edit your storefront's text and images below, then save - changes go live immediately.")}</p>
      </div>
      <a href={website} target="_blank" rel="noreferrer" className="btn-secondary inline-flex gap-2 items-center"><ExternalLink size={17} /> {t("View website")}</a>
    </header>

    {error && <div role="alert" className="p-4 bg-red-50 text-red-700 rounded-xl">{error} <button onClick={load} className="underline">{t("Retry")}</button></div>}

    <section className="rounded-2xl border bg-white p-6 flex flex-wrap items-center justify-between gap-4">
      <div>
        <span className="text-xs font-semibold uppercase tracking-wider text-emerald-700">{t("Layout")}</span>
        <h2 className="text-xl font-semibold mt-2">{t("Template")}</h2>
        <p className="text-sm text-slate-500 mt-1">{t("Choose the overall layout - the editor below customizes text, images and colors on top of it.")}</p>
      </div>
      <Link className="btn-secondary" to={`/admin/settings?tab=${templatesTab}`}>{t("Browse templates")}</Link>
    </section>

    <div className="grid lg:grid-cols-2 gap-6">
      <section className="rounded-2xl border bg-white p-6 space-y-5">
        <h2 className="text-xl font-semibold">{t("Edit your homepage")}</h2>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">{t("Headline")}</label>
          <input
            value={form.heroTitle}
            onChange={e => setForm(f => ({ ...f, heroTitle: e.target.value }))}
            placeholder={heroTitlePlaceholder}
            className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">{t("Subheading")}</label>
          <textarea
            value={form.heroSubtitle}
            onChange={e => setForm(f => ({ ...f, heroSubtitle: e.target.value }))}
            rows={2}
            className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">{t("Hero image")}</label>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingImage}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-dashed border-slate-300 text-slate-500 hover:border-emerald-500 hover:text-emerald-700 disabled:opacity-50"
            >
              <ImageIcon size={18} /> {uploadingImage ? t("Uploading...") : form.heroImage ? t("Change image") : t("Upload image")}
            </button>
            {form.heroImage && <img src={form.heroImage} alt="" className="w-14 h-14 rounded-lg object-cover border border-slate-200" />}
          </div>
          <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={e => handleImageUpload(e.target.files?.[0])} />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">{t("Colors")}</label>
          <div className="flex gap-4">
            {colorFields.map(c => (
              <label key={c.key} className="flex flex-col items-center gap-1.5 text-xs text-slate-500">
                <input
                  type="color"
                  value={form.colors?.[c.key] || c.fallback}
                  onChange={e => setForm(f => ({ ...f, colors: { ...f.colors, [c.key]: e.target.value } }))}
                  className="w-10 h-10 rounded-full border border-slate-200 cursor-pointer"
                />
                {t(c.label)}
              </label>
            ))}
          </div>
        </div>

        <button onClick={handleSave} disabled={saving} className="btn-primary w-full flex items-center justify-center gap-2">
          <Check size={17} /> {saving ? t("Saving...") : t("Save changes")}
        </button>
      </section>

      <section className="rounded-2xl border bg-white p-6">
        <span className="text-xs font-semibold uppercase tracking-wider text-emerald-700">{t("Live preview")}</span>
        <div
          className="rounded-xl p-8 mt-3 min-h-[280px] flex flex-col justify-center"
          style={{ background: form.colors?.secondary || '#eaece4', color: form.colors?.text || '#202925' }}
        >
          {form.heroImage && (
            <img src={form.heroImage} alt="" className="w-full h-32 object-cover rounded-lg mb-5" />
          )}
          <p className="text-[10px] font-bold tracking-widest uppercase" style={{ color: form.colors?.accent || '#9b6b32' }}>
            {branch.name}
          </p>
          <h3 className="text-2xl font-semibold mt-2" style={{ color: form.colors?.primary || '#263f32' }}>
            {form.heroTitle || heroTitlePlaceholder}
          </h3>
          <p className="text-sm mt-2 opacity-80">{form.heroSubtitle || t("Your subheading will appear here.")}</p>
          <button className="mt-5 self-start px-5 py-2 rounded-full text-sm font-medium text-white" style={{ background: form.colors?.primary || '#263f32' }}>
            {businessType === 'restaurant' ? t("Explore the menu") : businessType === 'ecommerce' ? t("Shop the collection") : t("Book now")}
          </button>
        </div>
        <p className="text-xs text-slate-400 mt-3">{t("This mirrors your homepage's hero section - the full page (product grid, categories, etc.) uses your selected template.")}</p>
      </section>
    </div>
  </div>
}
